import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import { authAPI } from '../lib/api';
import { userManager, isRedirectCallback, cleanCallbackUrl } from '../lib/oidcConfig';

const AuthContext = createContext(null);

/**
 * Deux jetons cohabitent, volontairement :
 *
 *  - l'access token Keycloak, qui prouve *qui* est l'utilisateur. Il ne sert
 *    qu'une fois, à l'ouverture de session ;
 *  - le jeton applicatif émis par l'API, présenté à chaque appel ensuite. Il
 *    porte le rôle déjà résolu et évite que chaque requête aille revalider un
 *    token auprès de Keycloak.
 *
 * Le rôle vient exclusivement du realm : il est réécrit à chaque connexion.
 * Retirer `salepulse-admin` dans Keycloak retire les droits ici dès la session
 * suivante ; l'application n'offre aucun moyen de se les redonner.
 */
export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const initialised = useRef(false);

  useEffect(() => {
    // React monte deux fois les effets en développement ; sans ce garde-fou le
    // code d'autorisation serait consommé deux fois et le second échange
    // échouerait avec une erreur incompréhensible.
    if (initialised.current) return;
    initialised.current = true;

    const init = async () => {
      try {
        if (isRedirectCallback()) {
          const oidcUser = await userManager.signinRedirectCallback();
          // Nettoyer l'URL avant l'échange : si celui-ci échoue, un
          // rechargement ne doit pas rejouer un code déjà consommé.
          cleanCallbackUrl();
          const { data } = await authAPI.exchange(oidcUser.access_token);
          localStorage.setItem('crm_token', data.access_token);
          setUser(data.user);
          setLoading(false);
          return;
        }

        const stored = localStorage.getItem('crm_token');
        if (stored) {
          const { data } = await authAPI.me();
          setUser(data);
        }
      } catch (err) {
        console.error('[Auth] initialisation :', err);
        cleanCallbackUrl();
        localStorage.removeItem('crm_token');
        localStorage.removeItem('crm_user');
        setUser(null);
        setError(err.response?.data?.detail || err.message || 'Échec de la connexion');
      } finally {
        setLoading(false);
      }
    };

    init();
  }, []);

  /** Redirige vers Keycloak, qui redirige lui-même vers Microsoft. */
  const login = useCallback(async () => {
    setError(null);
    await userManager.signinRedirect();
  }, []);

  const logout = useCallback(async () => {
    localStorage.removeItem('crm_token');
    localStorage.removeItem('crm_user');
    setUser(null);
    try {
      // Ferme aussi la session Keycloak, sinon un nouveau « se connecter »
      // reconnecterait silencieusement sans repasser par l'écran de connexion.
      await userManager.signoutRedirect();
    } catch {
      window.location.href = '/';
    }
  }, []);

  const value = {
    user,
    loading,
    error,
    login,
    logout,
    isAuthenticated: !!user,
    isAdmin: user?.role === 'admin',
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within an AuthProvider');
  return context;
}
